import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import ReactPlayer from 'react-player';
import AliceCarousel from 'react-alice-carousel';
import 'react-alice-carousel/lib/alice-carousel.css';
import { PhotoIcon, VideoCameraIcon, SparklesIcon } from '@heroicons/react/24/outline';
import Feed from '../components/landing/Feed';
import saree_video from '../assets/demo-content/1.mp4';
import gown_video from '../assets/demo-content/2.mp4';
import jewelry_img from '../assets/demo-content/3.jpg';
import jewelry_video from '../assets/demo-content/4.mp4';
import jewelry_img2 from '../assets/demo-content/5.jpg';

const ModelsTicker = () => {
  const models = useMemo(() => [
    'Google Imagen 4',
    'BFL FLUX Kontext',
    'Runway Gen-3',
    'OpenAI Sora',
    'Kling 2.2',
    'Seedance',
  ], []);

  return (
    <div className="relative overflow-hidden bg-gradient-to-r from-purple-900/20 via-transparent to-purple-900/20 rounded-xl border border-white/10">
      <style>{`
        @keyframes tickerMove { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .ticker-track { display: flex; width: 200%; animation: tickerMove 60s linear infinite; }
        .ticker-group { display: flex; gap: 1rem; width: 50%; align-items: center; }
      `}</style>
      <div className="absolute inset-0 bg-gradient-to-r from-black via-transparent to-black pointer-events-none" />
      <div className="ticker-track">
        <div className="ticker-group py-4">
          {models.map((m, i) => (
            <span key={`m1-${i}`} className="px-4 py-2 rounded-full bg-white/10 text-white/90 text-sm whitespace-nowrap border border-white/10">
              {m}
            </span>
          ))}
        </div>
        <div className="ticker-group py-4">
          {models.map((m, i) => (
            <span key={`m2-${i}`} className="px-4 py-2 rounded-full bg-white/10 text-white/90 text-sm whitespace-nowrap border border-white/10">
              {m}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const AiOptionCard = ({ to, title, description, Icon }) => (
  <Link to={to} className="group block rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors p-6 focus:outline-none focus:ring-2 focus:ring-purple-500">
    <div className="flex items-center gap-4">
      <div className="p-3 rounded-xl bg-purple-500/20 text-purple-300 border border-purple-500/30">
        <Icon className="w-7 h-7" />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-white">{title}</h3>
        <p className="text-sm text-white/70 mt-1">{description}</p>
      </div>
    </div>
  </Link>
);

const CommunityCarousel = () => {
  // Assets imported from `client/src/assets/demo-content`
  const items = useMemo(() => [
    { type: 'video', src: saree_video, alt: 'Video of an elegant Kancheepuram saree from a phone click' },
    { type: 'video', src: gown_video, alt: 'Video of an classy party gown from a phone click' },
    { type: 'image', src: jewelry_img, alt: 'Image of a classy model with black metal neck piece, from a phone click' },
    { type: 'video', src: jewelry_video, alt: 'Video of a classy model with black metal juhmka, from a phone click' },
    { type: 'image', src: jewelry_img2, alt: 'Image of a classy model with necklace, from a phone click' },
  ], []);

  const slides = items.map((item, index) => (
    <div key={index} className="w-full h-[420px] md:h-[520px] flex items-center justify-center bg-black">
      {item.type === 'image' ? (
        <img src={item.src} alt={item.alt} className="w-full h-full object-contain" />
      ) : (
        <div className="w-full h-full">
          <ReactPlayer
            url={item.src}
            width="100%"
            height="100%"
            controls={false}
            playing={true}
            muted={true}
            loop={true}
            playsinline={true}
            style={{ backgroundColor: 'black' }}
          />
        </div>
      )}
    </div>
  ));

  return (
    <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent p-2">
      <AliceCarousel
        mouseTracking
        items={slides}
        disableButtonsControls
        autoPlay
        autoPlayInterval={3500}
        animationDuration={900}
        infinite
        controlsStrategy="responsive"
      />
    </div>
  );
};

function HomeAuthed() {
  const aiOptions = [
    {
      to: '/image',
      title: 'Image',
      description: 'Click to start creating images for your products',
      Icon: PhotoIcon,
    },
    {
      to: '/video',
      title: 'Video',
      description: 'Create videos for your apparel and clothes',
      Icon: VideoCameraIcon,
    },
  ];

  return (
    <div className="px-6 md:px-10 pt-6 pb-16">
      {/* Hero / Intro */}
      <section className="mb-10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Create professional visuals, at scale</h1>
            <p className="text-white/70 mt-2">Images, videos and upscales tailored for Apparel, Jewelry, Furnitures and many more products</p>
          </div>
        </div>
      </section>

      {/* AI Options */}
      <section className="mb-12">
        {/* <h2 className="text-white/90 sfont-semibold mb-4">AI options in this project</h2> */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {aiOptions.map((opt) => (
            <AiOptionCard key={opt.title} {...opt} />
          ))}
        </div>
      </section>

      {/* Latest Models Ticker */}
      <section className="mb-12">
        <h2 className="text-white/90 font-semibold mb-4">Latest AI models</h2>
        <ModelsTicker />
      </section>

      {/* Gallery Feed */}
      <section className="-mx-6 md:-mx-10">
        <Feed />
      </section>
    </div>
  );
}

export default HomeAuthed; 